import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext } from 'react';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton, { ISkeletonProps } from '@ui/atoms/Skeleton';

import {
	IInsuranceProviderStepPageContent,
	InsuranceProviderStep,
} from '@ui/routes/Insurance/InsuranceProviderStep';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import useInsuranceProviders from '@hooks/useInsuranceProviders';

import { keysToCamel } from '@utils/butter';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';
import { IInsuranceProvider } from '@folx-types/insurance';

import { dataConstants } from './MembershipInsuranceProviderStep.data';

interface IInsuranceProviderStepPageProps extends IFlowStepProps {}

const InsuranceProviderStepPage: FC<IInsuranceProviderStepPageProps> = ({ path, stepKey }) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "insurance-provider-step" }) {
				page_header
				page_info
				select_insurance_label
			}
		}
	`);

	const { loading, data } = useInsuranceProviders();

	const { existingFormData, handleNavBack, handleFormSubmission } = useContext(FlowContext);

	const refinedContent: IInsuranceProviderStepPageContent = keysToCamel(
		pageContent
	) as IInsuranceProviderStepPageContent;

	const FullWidthColumn = fullWidth(FlexColumn);
	const SpacedSkeleton = spaced<ISkeletonProps>(Skeleton);

	const skeleton = (
		<FullWidthColumn gap="lg">
			<SpacedSkeleton height="xl" fullWidth marginBottom="xs" />
			<FlexColumn gap="sm">
				<Skeleton height="xxxl" fullWidth />
			</FlexColumn>
			<FlexColumn gap="sm">
				<Skeleton height="lg" fullWidth />
			</FlexColumn>
		</FullWidthColumn>
	);

	const handleInsuranceSubmission = (provider: IInsuranceProvider) => {
		handleFormSubmission(
			{
				[dataConstants.insuranceProvider]: provider?.id,
				[dataConstants.insuranceProviderName]: provider?.name,
				[dataConstants.insuranceInNetwork]: provider?.inNetwork,
			},
			stepKey
		);
	};

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<InsuranceProviderStep
				pageContent={refinedContent}
				path={path}
				stepKey={stepKey}
				existingFormData={existingFormData}
				handleInsuranceSubmission={handleInsuranceSubmission}
				handleNavBack={handleNavBack}
				insuranceProviders={data}
			/>
		</LoadingWrapper>
	);
};

export default InsuranceProviderStepPage;
